document.documentElement.classList.remove('no-js');

const header = document.querySelector('[data-site-header]');
const menuToggle = document.querySelector('[data-menu-toggle]');
const navigation = document.querySelector('[data-site-nav]');

const updateHeader = () => header?.classList.toggle('is-scrolled', window.scrollY > 24);
updateHeader();
window.addEventListener('scroll', updateHeader, { passive: true });

menuToggle?.addEventListener('click', () => {
  const open = menuToggle.getAttribute('aria-expanded') !== 'true';
  menuToggle.setAttribute('aria-expanded', String(open));
  navigation?.classList.toggle('is-open', open);
  document.body.classList.toggle('menu-open', open);
});

navigation?.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => {
  menuToggle?.setAttribute('aria-expanded', 'false');
  navigation.classList.remove('is-open');
  document.body.classList.remove('menu-open');
}));

document.querySelectorAll('a[href="#collections"], a[href="/#collections"]').forEach((link) => {
  link.addEventListener('click', (event) => {
    const target = document.querySelector('#collections');
    if (!target) return;
    event.preventDefault();
    history.replaceState(null, '', '#collections');
    target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  });
});

document.querySelectorAll('[data-hero-headlines]').forEach((headline) => {
  const items = [...headline.querySelectorAll('[data-hero-headline]')];
  const dust = headline.querySelector('[data-headline-dust]');
  const interval = Number(headline.dataset.interval) || 4000;
  const motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  let activeIndex = 0;
  let paused = false;
  let timer;

  const makeDust = () => {
    if (!dust || motionReduced.matches) return;
    dust.replaceChildren(...Array.from({ length: 46 }, (_, index) => {
      const particle = document.createElement('i');
      const size = 1 + Math.random() * 3.2;
      particle.className = 'hero-headline__particle';
      particle.style.setProperty('--x', `${Math.random() * 92}%`);
      particle.style.setProperty('--y', `${10 + Math.random() * 76}%`);
      particle.style.setProperty('--dx', `${75 + Math.random() * 230}px`);
      particle.style.setProperty('--dy', `${-55 + Math.random() * 115}px`);
      particle.style.setProperty('--delay', `${Math.random() * 240}ms`);
      particle.style.setProperty('--duration', `${620 + Math.random() * 520}ms`);
      particle.style.setProperty('--size', `${size}px`);
      particle.dataset.particle = String(index);
      return particle;
    }));
  };

  const schedule = (delay = interval) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(cycle, delay);
  };

  const cycle = () => {
    if (items.length < 2) return;
    if (paused || document.hidden || motionReduced.matches) {
      schedule(500);
      return;
    }

    const current = items[activeIndex];
    const nextIndex = (activeIndex + 1) % items.length;
    const next = items[nextIndex];
    makeDust();
    current.classList.add('is-leaving');

    window.setTimeout(() => {
      current.classList.remove('is-active', 'is-leaving');
      next.classList.add('is-active', 'is-entering');
      activeIndex = nextIndex;
      window.setTimeout(() => next.classList.remove('is-entering'), 900);
      window.setTimeout(() => dust?.replaceChildren(), 1250);
      schedule();
    }, motionReduced.matches ? 260 : 860);
  };

  headline.addEventListener('mouseenter', () => { paused = true; });
  headline.addEventListener('mouseleave', () => { paused = false; schedule(600); });
  headline.addEventListener('focusin', () => { paused = true; });
  headline.addEventListener('focusout', () => { paused = false; schedule(600); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(600); });
  schedule();
});

document.querySelectorAll('[data-craft-carousel]').forEach((carousel) => {
  const slides = [...carousel.querySelectorAll('[data-craft-slide]')];
  const titles = [...carousel.querySelectorAll('[data-craft-title]')];
  const dots = [...carousel.querySelectorAll('[data-craft-dot]')];
  const motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  let activeIndex = 0;
  let paused = false;

  if (slides.length < 2 || motionReduced.matches) return;

  const advance = () => {
    if (paused || document.hidden) return;
    slides[activeIndex]?.classList.remove('is-active');
    titles[activeIndex]?.classList.remove('is-active');
    dots[activeIndex]?.classList.remove('is-active');
    activeIndex = (activeIndex + 1) % slides.length;
    slides[activeIndex]?.classList.add('is-active');
    titles[activeIndex]?.classList.add('is-active');
    dots[activeIndex]?.classList.add('is-active');
  };

  const timer = window.setInterval(advance, 3800);
  carousel.addEventListener('mouseenter', () => { paused = true; });
  carousel.addEventListener('mouseleave', () => { paused = false; });
  carousel.addEventListener('focusin', () => { paused = true; });
  carousel.addEventListener('focusout', () => { paused = false; });
  window.addEventListener('pagehide', () => window.clearInterval(timer), { once: true });
});

const celestialTransitions = [...document.querySelectorAll('[data-celestial-parallax]')];
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
let parallaxTicking = false;

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const smoothstep = (value) => value * value * (3 - (2 * value));

const celestialMotion = celestialTransitions.map((section) => ({ section, current: 0, target: 0 }));

const renderCelestialParallax = () => {
  let keepAnimating = false;

  celestialMotion.forEach((motion) => {
    const { section } = motion;
    const difference = motion.target - motion.current;

    if (reducedMotion.matches || Math.abs(difference) < .001) {
      motion.current = motion.target;
    } else {
      motion.current += difference * .22;
      keepAnimating = true;
    }

    const progress = smoothstep(motion.current);
    const rect = section.getBoundingClientRect();
    section.style.setProperty('--celestial-progress', progress.toFixed(4));
    section.style.setProperty('--warm-opacity', (1 - progress).toFixed(4));
    section.style.setProperty('--nebula-opacity', clamp((progress - .04) / .72).toFixed(4));
    section.style.setProperty('--star-opacity', clamp((progress - .16) / .58).toFixed(4));
    section.style.setProperty('--dust-opacity', (clamp((progress - .2) / .58) * .62).toFixed(4));
    section.style.setProperty('--far-shift', `${(-progress * 24).toFixed(2)}px`);
    section.style.setProperty('--near-shift', `${(-progress * 48).toFixed(2)}px`);
    section.style.setProperty('--dust-shift', `${(-progress * 22).toFixed(2)}px`);
    section.style.setProperty('--nebula-shift', `${(12 - progress * 26).toFixed(2)}px`);
    section.style.setProperty('--warm-copy-opacity', (1 - clamp(progress / .58)).toFixed(4));
    section.style.setProperty('--cool-copy-opacity', clamp((progress - .34) / .42).toFixed(4));
    section.style.setProperty('--warm-copy-shift', `${(-progress * 9).toFixed(2)}px`);
    section.style.setProperty('--cool-copy-shift', `${((1 - progress) * 9).toFixed(2)}px`);
    section.style.setProperty('--line-scale', (.18 + progress * .82).toFixed(4));
    section.style.setProperty('--orbit-opacity', clamp((progress - .22) / .5).toFixed(4));
    section.style.setProperty('--constellation-progress', clamp((progress - .3) / .48).toFixed(4));
    section.style.setProperty('--ember-opacity', (1 - clamp(progress * 1.8)).toFixed(4));
  });

  if (keepAnimating) {
    requestAnimationFrame(renderCelestialParallax);
  } else {
    parallaxTicking = false;
  }
};

const updateCelestialTargets = () => {
  celestialMotion.forEach((motion) => {
    const rect = motion.section.getBoundingClientRect();
    if (motion.section.dataset.celestialMode === 'viewport') {
      const travel = Math.max(1, rect.height + (window.innerHeight * .5));
      motion.target = reducedMotion.matches ? 1 : clamp(((window.innerHeight * .76) - rect.top) / travel);
    } else {
      const travel = Math.max(1, rect.height - window.innerHeight);
      motion.target = reducedMotion.matches ? 1 : clamp(-rect.top / travel);
    }
  });

  if (!parallaxTicking) {
    parallaxTicking = true;
    requestAnimationFrame(renderCelestialParallax);
  }
};

if (celestialTransitions.length) {
  updateCelestialTargets();
  window.addEventListener('scroll', updateCelestialTargets, { passive: true });
  window.addEventListener('resize', updateCelestialTargets, { passive: true });
  reducedMotion.addEventListener?.('change', updateCelestialTargets);
}

document.querySelectorAll('[data-portrait-catalogue]').forEach((catalogue) => {
  const filters = [...catalogue.querySelectorAll('[data-gallery-filter]')];
  const cards = [...catalogue.querySelectorAll('[data-gallery-card]')];
  const count = catalogue.querySelector('[data-gallery-count]');
  const dialog = catalogue.querySelector('[data-gallery-dialog]');
  const dialogImage = catalogue.querySelector('[data-gallery-dialog-image]');
  const dialogTitle = catalogue.querySelector('[data-gallery-dialog-title]');
  const dialogDescription = catalogue.querySelector('[data-gallery-dialog-description]');
  const dialogCategories = catalogue.querySelector('[data-gallery-dialog-categories]');
  const dialogCta = catalogue.querySelector('[data-gallery-dialog-cta]');

  filters.forEach((button) => button.addEventListener('click', () => {
    const selected = button.dataset.galleryFilter;
    let visible = 0;

    filters.forEach((filter) => {
      const active = filter === button;
      filter.classList.toggle('is-active', active);
      filter.setAttribute('aria-pressed', String(active));
    });

    cards.forEach((card) => {
      const categories = card.dataset.categories.split(/\s+/).filter(Boolean);
      const show = selected === 'all' || categories.includes(selected);
      card.hidden = !show;
      if (show) visible += 1;
    });

    if (count) count.textContent = String(visible);
  }));

  catalogue.querySelectorAll('[data-gallery-open]').forEach((button) => button.addEventListener('click', () => {
    if (!dialog) return;
    const labels = button.dataset.categories.split(/\s+/).filter(Boolean);
    dialogImage.src = button.dataset.image;
    dialogImage.alt = `${button.dataset.title} portrait style`;
    dialogTitle.textContent = button.dataset.title;
    dialogDescription.textContent = button.dataset.description || 'A signature IntoTheCanvas portrait style, composed around the person at its centre and refined by hand before print.';
    if (dialogCta) {
      const productPage = button.dataset.productPage;
      dialogCta.href = productPage || dialogCta.dataset.defaultHref || '/pages/how-it-works';
      dialogCta.textContent = productPage ? 'Enter this world' : (dialogCta.dataset.defaultLabel || 'Choose this style');
    }
    dialogCategories.replaceChildren(...labels.map((label) => {
      const tag = document.createElement('span');
      tag.textContent = label.charAt(0).toUpperCase() + label.slice(1);
      return tag;
    }));
    dialog.showModal();
  }));

  catalogue.querySelector('[data-gallery-close]')?.addEventListener('click', () => dialog?.close());
  dialog?.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
});


